import type { TabSettings } from '../../../../hooks';
import { SystemPermission } from '../../../../../context';
import { ActiveTabKey, NavItemConfig } from './types';

import chartbarIcon from '../../../../../assets/img/chartbar.png';
import greyBellIcon from '../../../../../assets/img/greyBell.png';
import greyArrowIcon from '../../../../../assets/img/greyArrow.png';
import listIcon from '../../../../../assets/img/list.png';
import sIcon from '../../../../../assets/img/s.png';
import chatBubbleIcon from '../../../../../assets/img/r.png';

export interface NavItemsConfig {
  patientProfile: {
    first_name?: string;
  };
  tabSettings: TabSettings;
  handleSelection: (selection: ActiveTabKey) => void;
  activeInsightsCnt?: number;
  actionsCnt?: number;
  areActionsEnabled?: boolean;
  permissionsSetup?: boolean;
  permissions?: Partial<Record<SystemPermission, boolean>>;
}

export const createNavItems = (config: NavItemsConfig): NavItemConfig[] => {
  const {
    patientProfile,
    tabSettings,
    handleSelection,
    activeInsightsCnt = 0,
    actionsCnt = 0,
    areActionsEnabled = true,
    permissionsSetup = false,
    permissions,
  } = config;
  const hasDeidentifiedPermission =
    !!permissionsSetup && !!permissions?.[SystemPermission.DEIDENTIFIED_DATA];

  const getPatientName = () => {
    if (!patientProfile.first_name) return '';
    const name = patientProfile.first_name;
    const possessive = name.endsWith('s') ? name + "'" : name + "'s";
    return possessive;
  };

  return [
    {
      id: ActiveTabKey.Timeline,
      href: '#' + ActiveTabKey.Timeline,
      onClick: () => handleSelection(ActiveTabKey.Timeline),
      icon: chartbarIcon,
      iconAlt: 'chartbar',
      label: `${getPatientName()} Timeline`,
      isVisible: tabSettings.showPatientTimeline,
    },
    {
      id: ActiveTabKey.InsightsAndActions,
      href: '#' + ActiveTabKey.InsightsAndActions,
      onClick: () => handleSelection(ActiveTabKey.InsightsAndActions),
      icon: greyBellIcon,
      iconAlt: 'grey Bell',
      label: areActionsEnabled ? 'Insights and actions' : 'Insights',
      badge: {
        count: activeInsightsCnt + actionsCnt,
        className: 'dotPinkMed',
      },
      isVisible: tabSettings.showInsightsAndActions,
    },
    {
      id: ActiveTabKey.Predictions,
      href: '#' + ActiveTabKey.Predictions,
      onClick: () => handleSelection(ActiveTabKey.Predictions),
      icon: greyArrowIcon,
      iconAlt: 'grey arrow',
      label: 'Predictions',
      isVisible: tabSettings.showPredictions,
    },
    {
      id: ActiveTabKey.SharedWellbeingPlan,
      href: '#' + ActiveTabKey.SharedWellbeingPlan,
      onClick: () => handleSelection(ActiveTabKey.SharedWellbeingPlan),
      icon: listIcon,
      iconAlt: 'chartbar',
      label: 'Shared wellbeing plan',
      isVisible: tabSettings.showSharedWellbeingPlan,
    },
    {
      id: ActiveTabKey.InsightSettings,
      href: '#' + ActiveTabKey.InsightSettings,
      onClick: () => handleSelection(ActiveTabKey.InsightSettings),
      icon: sIcon,
      iconAlt: 'chartbar',
      label: 'Insight settings',
      isVisible: tabSettings.showInsightSettings,
    },
    {
      id: ActiveTabKey.InvitePatient,
      href: '#' + ActiveTabKey.InvitePatient,
      onClick: () => handleSelection(ActiveTabKey.InvitePatient),
      icon: sIcon,
      iconAlt: 'chartbar',
      label: 'mypainhealth app',
      isVisible: tabSettings.showInvitePatient && !hasDeidentifiedPermission,
    },
    {
      id: ActiveTabKey.OsaNavigatorApp,
      href: '#' + ActiveTabKey.OsaNavigatorApp,
      onClick: () => handleSelection(ActiveTabKey.OsaNavigatorApp),
      icon: chatBubbleIcon,
      iconAlt: 'chartbar',
      label: 'OSA Navigator app',
      isVisible: tabSettings.showOsaNavigatorApp,
    },
    {
      id: ActiveTabKey.InterventionSettings,
      href: '#' + ActiveTabKey.InterventionSettings,
      onClick: () => handleSelection(ActiveTabKey.InterventionSettings),
      icon: sIcon,
      iconAlt: 'chartbar',
      label: 'Intervention settings',
      isVisible: tabSettings.showInterventionSettings,
    },
  ];
};
